import { supabase } from '../supabase/client';
import { API_CONFIG, getApiUrl } from '../config';

/**
 * Get the base headers for API requests including auth
 * @returns {Object} Headers object
 */
const getBaseHeaders = async () => {
  // Get the session token from Supabase
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Gets the current user's details from the API
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const getUserDetails = async () => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_USER_DETAILS);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const userData = await response.json();
    return { data: userData, error: null };
  } catch (error) {
    console.error('Error getting user details:', error);
    return { data: null, error };
  }
};

/**
 * Gets the user's onboarding status
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const getOnboardingStatus = async () => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.GET_ONBOARDING_STATUS);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return { data: null, error };
  }
};

/**
 * Gets the list of available games
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const getGamesList = async () => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.LIST_GAMES);
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const games = await response.json();
    return { data: games, error: null };
  } catch (error) {
    console.error('Error fetching games list:', error);
    return { data: null, error };
  }
};

/**
 * Compatibility layer for old getUserProfile function
 * Maps the new API response format to the old format for backward compatibility
 * @returns {Promise<Object>} Object containing data and error properties in the old format
 */
export const getUserProfile = async () => {
  const { data, error } = await getUserDetails();
  
  if (error) {
    return { data: null, error };
  }
  
  // Map the new API response to the old format expected by existing code
  const mappedData = {
    First_Name: data.firstName,
    Last_Name: data.lastName,
    Preferred_Name: data.preferredName,
    Gender: data.gender,
    Email: data.email,
    Age: data.age,
    Region: data.region,
    Game_ID: data.gameId,
    Games_List: {
      Game_Name: data.gameName
    }
  };
  
  return { data: mappedData, error: null };
};

/**
 * Updates the current user's details
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const updateUserDetails = async (userData) => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.UPDATE_USER_DETAILS);
    
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers,
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const updatedUserData = await response.json();
    return { data: updatedUserData, error: null };
  } catch (error) {
    console.error('Error updating user details:', error);
    return { data: null, error };
  }
};

/**
 * Completes the user onboarding process
 * @param {Object} onboardingData - Data collected during the onboarding process
 * @returns {Promise<Object>} Object containing data and error properties
 */
export const completeOnboarding = async (onboardingData) => {
  try {
    const headers = await getBaseHeaders();
    const apiUrl = getApiUrl(API_CONFIG.POKKETAPI.ENDPOINTS.COMPLETE_ONBOARDING);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(onboardingData)
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.error('Error completing onboarding:', error);
    return { data: null, error };
  }
}; 